import React, { useEffect, useRef, useState } from 'react';
import { flipClamp } from '~utils/number';
import { CarouselContext } from './CarouselContext';
import { createConfig, defaultCarouselConfig } from './config';

import type {
  InternalState,
  NavigationStartHandler,
  NavigationEndHandler,
  CarouselConfig,
  CarouselState,
  NavigationFn,
} from './types';

type CarouselProps = {
  children?: React.ReactNode;
  config?: CarouselConfig;
  onNavigationStart?: NavigationStartHandler;
  onNavigationEnd?: NavigationEndHandler;
};

export const Carousel: React.FC<CarouselProps> = ({
  children,
  config: passedConfig = defaultCarouselConfig,
  onNavigationStart,
  onNavigationEnd,
}) => {
  const [slideCount, setSlideCount] = useState<number>(0);
  const [carouselState, setCarouselState] = useState<CarouselState>({
    currentIndex: 0,
    currentConfig: createConfig(passedConfig),
  });

  const internalState = useRef<InternalState>({
    slideCount: NaN,
    pageCount: NaN,
    prevIndex: NaN,
    allowNavigation: true,
    navigationDelta: 1,
    navigationMode: 'manual',
    autoplayPaused: false,
  });

  const {
    currentIndex,
    currentConfig: { slidesToShow, autoplay, autoplayInterval, autoplayDirection },
  } = carouselState;

  // Effect to handle config update on media query change
  useEffect(() => {
    setCarouselState((state) => ({ ...state, currentConfig: createConfig(passedConfig) }));

    const mediaQueryListCollection: MediaQueryList[] = [];

    const changeHandler = () => {
      internalState.current.prevIndex = NaN;
      internalState.current.allowNavigation = true;
      setCarouselState({ currentIndex: 0, currentConfig: createConfig(passedConfig) });
    };

    for (const query in passedConfig) {
      const mediaQueryList = matchMedia(query);
      mediaQueryList.addEventListener('change', changeHandler);
      mediaQueryListCollection.push(mediaQueryList);
    }

    return () => {
      mediaQueryListCollection.forEach((mediaQueryList) => {
        mediaQueryList.removeEventListener('change', changeHandler);
      });
    };
  }, [passedConfig]);

  // Effect to start/stop autoplay timeout
  useEffect(() => {
    startAutoplay();
    return stopAutoplay;
  }, [carouselState]);

  const navigate: NavigationFn = (delta, mode = 'manual') => {
    if (!internalState.current.allowNavigation) return;

    internalState.current.prevIndex = currentIndex;
    internalState.current.navigationDelta = delta;
    internalState.current.navigationMode = mode;
    const nextIndex = flipClamp(currentIndex + delta, 0, internalState.current.pageCount - 1);
    setCarouselState((state) => ({ ...state, currentIndex: nextIndex }));
    onNavigationStart?.({ currentIndex, nextIndex });
  };

  const startAutoplay = () => {
    if (autoplay && !internalState.current.autoplayPaused && slidesToShow < internalState.current.slideCount) {
      internalState.current.timeoutRef = setTimeout(() => {
        navigate(autoplayDirection === 'left' ? 1 : -1, 'autoplay');
      }, autoplayInterval);
    }
  };

  const stopAutoplay = () => clearTimeout(internalState.current.timeoutRef);

  return (
    <CarouselContext.Provider
      value={{
        slideCount,
        carouselState,
        internalState,
        setSlideCount,
        navigate,
        onNavigationStart,
        onNavigationEnd,
        startAutoplay,
        stopAutoplay,
      }}
    >
      {children}
    </CarouselContext.Provider>
  );
};
